import { useMemo } from "react";
import {
  useDataStudioListOnboardingJobsQuery,
  useGetJobMetadataQuery,
  useGetOnboardingJobLogsQuery,
} from "~/graphql/supergraphHooks";
import {
  type DataStudioListOnboardingJobsQueryVariables,
  type GetOnboardingJobLogsQueryVariables,
  type DataStudioListOnboardingJobsFilterInput,
} from "~/graphql/supergraphTypes";
import { DEFAULT_PAGE_LIMIT, DEFAULT_LOGS_LIMIT } from "./constants";
import {
  OnboardingJobRaw,
  OnboardingJob,
  DataStudioListOnboardingJobsResponse,
  ParsedJobConfig,
} from "./types";

// Helper function to parse and transform jobs
const transformJob = (rawJob: OnboardingJobRaw): OnboardingJob => {
  let parsedConfig: ParsedJobConfig = {};
  try {
    parsedConfig = JSON.parse(rawJob.jobConfig || "{}");
  } catch {
    // Silently handle parse errors - jobConfig is optional
  }

  const transformed = {
    ...rawJob,
    customerName: rawJob.arenaDomain,
    customerId: rawJob.arenaDomain,
    onboardingManager: {
      name: rawJob.createdBy || "",
    },
    tools: parsedConfig.tools,
  };

  return transformed;
};

// Hook for listing onboarding jobs with pagination support
export const useListOnboardingJobs = (variables?: {
  limit?: number;
  cursor?: string;
  filters?: Omit<DataStudioListOnboardingJobsFilterInput, "cursor">;
}) => {
  // Pass limit and filters parameters
  const queryVariables: DataStudioListOnboardingJobsQueryVariables = {
    limit: variables?.limit ?? DEFAULT_PAGE_LIMIT,
    filters: {
      ...(variables?.filters || {}),
      ...(variables?.cursor ? { cursor: variables.cursor } : {}),
    },
  };

  const queryResult = useDataStudioListOnboardingJobsQuery({
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true, // Important for fetchMore to trigger loading states
  });

  // Transform the response to include parsed jobConfig
  const transformedData: DataStudioListOnboardingJobsResponse | undefined = useMemo(() => {
    if (!queryResult.data) return undefined;

    const currentJobs = queryResult.data.dataStudioListOnboardingJobs.jobs;
    const transformedJobs = currentJobs.map((job) => transformJob(job));

    return {
      listOnboardingJobs: {
        ...queryResult.data.dataStudioListOnboardingJobs,
        jobs: transformedJobs,
      },
    };
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
  };
};

// Hook for getting job metadata
export const useGetJobMetadata = () => {
  const queryResult = useGetJobMetadataQuery({
    fetchPolicy: "cache-and-network",
  });

  return queryResult;
};

// Hook for getting job logs
export const useGetOnboardingJobLogs = (
  jobId: string,
  createdAt: string,
  options?: Omit<GetOnboardingJobLogsQueryVariables, "jobId" | "createdAt">,
) => {
  return useGetOnboardingJobLogsQuery({
    variables: {
      jobId,
      createdAt,
      taskId: options?.taskId,
      limit: options?.limit || DEFAULT_LOGS_LIMIT,
    },
    skip: !jobId || !createdAt,
    fetchPolicy: "cache-and-network",
  });
};
